import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { LoadingContainer } from '../components/ui/Spinner';
import { formatCurrency, formatDateTime } from '../utils/formatting';
import { ChevronDownIcon, ChevronUpIcon, EyeIcon, FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline';

const DECISION_QUALITY_COLORS = {
    correct: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    mistake: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
    neutral: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
    pending: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300',
};

// Map tag colors to Tailwind classes
const TAG_COLOR_CLASSES = {
    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    green: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    red: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
    purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
    orange: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300',
    yellow: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300',
    pink: 'bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300',
    indigo: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300',
    teal: 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300',
    emerald: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300',
    cyan: 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300',
    amber: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
    violet: 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300',
    gray: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
};

function HandsPage() {
    const [hands, setHands] = useState([]);
    const [leaks, setLeaks] = useState([]);
    const [players, setPlayers] = useState([]);
    const [availableTags, setAvailableTags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showFilters, setShowFilters] = useState(false);

    // Filters
    const [selectedDecision, setSelectedDecision] = useState('all');
    const [selectedTag, setSelectedTag] = useState('all');
    const [selectedReviewed, setSelectedReviewed] = useState('all');
    const [selectedPlayer, setSelectedPlayer] = useState('all');
    const [selectedLocation, setSelectedLocation] = useState('all');
    const [selectedLeak, setSelectedLeak] = useState('all');
    const [searchText, setSearchText] = useState('');
    const [minAmount, setMinAmount] = useState('');
    const [maxAmount, setMaxAmount] = useState('');

    // Sort
    const [sortField, setSortField] = useState('session_date');
    const [sortDirection, setSortDirection] = useState('desc');

    useEffect(() => {
        fetchHands();
        fetchLeaks();
        fetchPlayers();
        fetchTags();
    }, []);

    const fetchHands = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get('/api/hands/');
            setHands(response.data?.results || response.data || []);
        } catch (err) {
            console.error('Failed to fetch hands:', err);
            setError('Failed to load hands');
        } finally {
            setLoading(false);
        }
    };

    const fetchLeaks = async () => {
        try {
            const response = await axios.get('/api/leaks/');
            setLeaks(response.data?.results || response.data || []);
        } catch (err) {
            console.error('Failed to fetch leaks:', err);
        }
    };

    const fetchPlayers = async () => {
        try {
            const response = await axios.get('/api/players/');
            setPlayers(response.data?.results || response.data || []);
        } catch (err) {
            console.error('Failed to fetch players:', err);
        }
    };

    const fetchTags = async () => {
        try {
            const response = await axios.get('/api/hand-tags/');
            setAvailableTags(response.data?.results || response.data || []);
        } catch (err) {
            console.error('Failed to fetch tags:', err);
        }
    };

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('desc');
        }
    };

    const getSortIcon = (field) => {
        if (sortField !== field) return null;
        return sortDirection === 'asc' ? (
            <ChevronUpIcon className="h-4 w-4 inline ml-1" />
        ) : (
            <ChevronDownIcon className="h-4 w-4 inline ml-1" />
        );
    };

    const clearFilters = () => {
        setSelectedDecision('all');
        setSelectedTag('all');
        setSelectedReviewed('all');
        setSelectedPlayer('all');
        setSelectedLocation('all');
        setSelectedLeak('all');
        setSearchText('');
        setMinAmount('');
        setMaxAmount('');
    };

    // Count active filters
    const activeFilterCount = [
        selectedDecision !== 'all',
        selectedTag !== 'all',
        selectedReviewed !== 'all',
        selectedPlayer !== 'all',
        selectedLocation !== 'all',
        selectedLeak !== 'all',
        searchText !== '',
        minAmount !== '',
        maxAmount !== '',
    ].filter(Boolean).length;

    // Get tag color class
    const getTagColorClass = (tagName) => {
        const tag = availableTags.find(t => t.name === tagName);
        return TAG_COLOR_CLASSES[tag?.color] || TAG_COLOR_CLASSES.gray;
    };

    // Filter hands
    const filteredHands = useMemo(() => {
        return hands.filter(hand => {
            // Decision quality
            if (selectedDecision !== 'all' && hand.decision_quality !== selectedDecision) {
                return false;
            }
            // Tag filter
            if (selectedTag !== 'all') {
                const hasTag = hand.tags?.includes(selectedTag);
                if (!hasTag) return false;
            }
            // Reviewed status
            if (selectedReviewed === 'reviewed' && !hand.reviewed) {
                return false;
            }
            if (selectedReviewed === 'unreviewed' && hand.reviewed) {
                return false;
            }
            // Player
            if (selectedPlayer !== 'all') {
                const hasPlayer = hand.players_involved?.some(p => p.id === parseInt(selectedPlayer));
                if (!hasPlayer) return false;
            }
            // Location
            if (selectedLocation !== 'all' && hand.session_location !== selectedLocation) {
                return false;
            }
            // Leak
            if (selectedLeak !== 'all') {
                const hasLeak = hand.leaks?.some(l => l.id === parseInt(selectedLeak));
                if (!hasLeak) return false;
            }
            // Search text
            if (searchText) {
                const search = searchText.toLowerCase();
                const inDescription = hand.hand_description?.toLowerCase().includes(search);
                const inNotes = hand.notes?.toLowerCase().includes(search);
                const inAnalysis = hand.review_analysis?.toLowerCase().includes(search);
                const inTags = hand.tags?.some(t => t.toLowerCase().includes(search));
                if (!inDescription && !inNotes && !inAnalysis && !inTags) return false;
            }
            // Amount range
            const amount = parseFloat(hand.amount) || 0;
            if (minAmount && amount < parseFloat(minAmount)) return false;
            if (maxAmount && amount > parseFloat(maxAmount)) return false;

            return true;
        });
    }, [hands, selectedDecision, selectedTag, selectedReviewed, selectedPlayer, selectedLocation, selectedLeak, searchText, minAmount, maxAmount]);

    // Sort hands
    const sortedHands = useMemo(() => {
        return [...filteredHands].sort((a, b) => {
            let aValue = a[sortField];
            let bValue = b[sortField];

            if (sortField === 'session_date') {
                aValue = new Date(aValue || 0);
                bValue = new Date(bValue || 0);
            } else if (sortField === 'amount') {
                aValue = parseFloat(aValue || 0);
                bValue = parseFloat(bValue || 0);
            }

            if (sortDirection === 'asc') {
                return aValue > bValue ? 1 : -1;
            } else {
                return aValue < bValue ? 1 : -1;
            }
        });
    }, [filteredHands, sortField, sortDirection]);

    // Calculate statistics based on filtered results
    const stats = useMemo(() => ({
        total: filteredHands.length,
        mistakes: filteredHands.filter(h => h.decision_quality === 'mistake').length,
        correct: filteredHands.filter(h => h.decision_quality === 'correct').length,
        pending: filteredHands.filter(h => h.decision_quality === 'pending' || !h.reviewed).length,
        totalAmount: filteredHands.reduce((sum, h) => {
            const amt = parseFloat(h.amount) || 0;
            return sum + (h.outcome === 'won' ? amt : h.outcome === 'lost' ? -amt : 0);
        }, 0),
    }), [filteredHands]);

    // Get unique locations from hands
    const handLocations = useMemo(() => {
        const locs = [...new Set(hands.map(h => h.session_location).filter(Boolean))];
        return locs.sort();
    }, [hands]);

    // Get unique tags from hands (for filter dropdown)
    const handTags = useMemo(() => {
        const tagSet = new Set();
        hands.forEach(h => {
            if (h.tags) {
                h.tags.forEach(t => tagSet.add(t));
            }
        });
        return [...tagSet].sort();
    }, [hands]);

    if (loading) {
        return (
            <div className="flex-grow px-4 py-4 mx-auto w-full max-w-6xl">
                <LoadingContainer />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex-grow px-4 py-4 mx-auto w-full max-w-6xl">
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <p className="text-red-600 dark:text-red-400">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex-grow px-4 py-4 mx-auto w-full max-w-6xl">
            {/* Header */}
            <div className="mb-6 flex items-start justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">All Hands</h1>
                    <p className="text-gray-600 dark:text-gray-400">
                        Review all notable hands logged across sessions
                    </p>
                </div>
                <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                        showFilters || activeFilterCount > 0
                            ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-300 dark:border-primary-700 text-primary-700 dark:text-primary-300'
                            : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                >
                    <FunnelIcon className="h-5 w-5" />
                    Filters
                    {activeFilterCount > 0 && (
                        <span className="bg-primary-600 text-white text-xs px-2 py-0.5 rounded-full">
                            {activeFilterCount}
                        </span>
                    )}
                </button>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Showing</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
                </div>
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Mistakes</p>
                    <p className="text-2xl font-bold text-red-600">{stats.mistakes}</p>
                </div>
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Correct</p>
                    <p className="text-2xl font-bold text-green-600">{stats.correct}</p>
                </div>
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Pending</p>
                    <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
                </div>
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Net Amount</p>
                    <p className={`text-2xl font-bold ${stats.totalAmount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {stats.totalAmount >= 0 ? '+' : ''}{formatCurrency(stats.totalAmount)}
                    </p>
                </div>
            </div>

            {/* Filters Panel */}
            {showFilters && (
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-medium text-gray-900 dark:text-white">Filters</h3>
                        {activeFilterCount > 0 && (
                            <button
                                onClick={clearFilters}
                                className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                            >
                                <XMarkIcon className="h-4 w-4" />
                                Clear all
                            </button>
                        )}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {/* Search */}
                        <div className="col-span-2">
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                Search
                            </label>
                            <input
                                type="text"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                placeholder="Search in description, notes, tags..."
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                            />
                        </div>

                        {/* Decision Quality */}
                        <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                Decision
                            </label>
                            <select
                                value={selectedDecision}
                                onChange={(e) => setSelectedDecision(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                            >
                                <option value="all">All</option>
                                <option value="mistake">Mistakes</option>
                                <option value="correct">Correct</option>
                                <option value="neutral">Neutral</option>
                                <option value="pending">Pending</option>
                            </select>
                        </div>

                        {/* Tag */}
                        <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                Tag
                            </label>
                            <select
                                value={selectedTag}
                                onChange={(e) => setSelectedTag(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                            >
                                <option value="all">All Tags</option>
                                {handTags.map(tag => (
                                    <option key={tag} value={tag}>{tag}</option>
                                ))}
                            </select>
                        </div>

                        {/* Player */}
                        <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                Player
                            </label>
                            <select
                                value={selectedPlayer}
                                onChange={(e) => setSelectedPlayer(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                            >
                                <option value="all">All Players</option>
                                {players.map(player => (
                                    <option key={player.id} value={player.id}>{player.name}</option>
                                ))}
                            </select>
                        </div>

                        {/* Location */}
                        <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                Location
                            </label>
                            <select
                                value={selectedLocation}
                                onChange={(e) => setSelectedLocation(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                            >
                                <option value="all">All Locations</option>
                                {handLocations.map(loc => (
                                    <option key={loc} value={loc}>{loc}</option>
                                ))}
                            </select>
                        </div>

                        {/* Leak */}
                        <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                Leak
                            </label>
                            <select
                                value={selectedLeak}
                                onChange={(e) => setSelectedLeak(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                            >
                                <option value="all">All Leaks</option>
                                {leaks.map(leak => (
                                    <option key={leak.id} value={leak.id}>{leak.name}</option>
                                ))}
                            </select>
                        </div>

                        {/* Reviewed Status */}
                        <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                Status
                            </label>
                            <select
                                value={selectedReviewed}
                                onChange={(e) => setSelectedReviewed(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                            >
                                <option value="all">All</option>
                                <option value="reviewed">Reviewed</option>
                                <option value="unreviewed">Unreviewed</option>
                            </select>
                        </div>

                        {/* Amount Range */}
                        <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                Min Amount
                            </label>
                            <input
                                type="number"
                                value={minAmount}
                                onChange={(e) => setMinAmount(e.target.value)}
                                placeholder="$0"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                Max Amount
                            </label>
                            <input
                                type="number"
                                value={maxAmount}
                                onChange={(e) => setMaxAmount(e.target.value)}
                                placeholder="No limit"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Results count */}
            <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                    {sortedHands.length === hands.length
                        ? `${hands.length} hands`
                        : `${sortedHands.length} of ${hands.length} hands`
                    }
                </span>
            </div>

            {/* Hands Table */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm overflow-hidden">
                {sortedHands.length === 0 ? (
                    <div className="p-8 text-center">
                        <p className="text-gray-600 dark:text-gray-400">
                            No hands found matching your filters
                        </p>
                        {activeFilterCount > 0 && (
                            <button
                                onClick={clearFilters}
                                className="mt-2 text-primary-600 dark:text-primary-400 hover:underline"
                            >
                                Clear filters
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left">
                            <thead className="text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 uppercase">
                                <tr>
                                    <th
                                        scope="col"
                                        className="px-4 py-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                                        onClick={() => handleSort('session_date')}
                                    >
                                        Session {getSortIcon('session_date')}
                                    </th>
                                    <th scope="col" className="px-4 py-3">Tags</th>
                                    <th scope="col" className="px-4 py-3">Description</th>
                                    <th scope="col" className="px-4 py-3">Decision</th>
                                    <th scope="col" className="px-4 py-3">Players</th>
                                    <th scope="col" className="px-4 py-3">Leaks</th>
                                    <th
                                        scope="col"
                                        className="px-4 py-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                                        onClick={() => handleSort('amount')}
                                    >
                                        Amount {getSortIcon('amount')}
                                    </th>
                                    <th scope="col" className="px-4 py-3"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedHands.map(hand => (
                                    <tr
                                        key={hand.id}
                                        className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                                    >
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="text-gray-900 dark:text-white">
                                                {formatDateTime(hand.session_date)}
                                            </div>
                                            {hand.session_location && (
                                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                                    {hand.session_location}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-wrap gap-1">
                                                {hand.tags?.slice(0, 2).map(tag => (
                                                    <span key={tag} className={`px-2 py-0.5 rounded text-xs ${getTagColorClass(tag)}`}>
                                                        {tag}
                                                    </span>
                                                ))}
                                                {hand.tags?.length > 2 && (
                                                    <span className="text-xs text-gray-500">+{hand.tags.length - 2}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300 max-w-md">
                                            <div className="line-clamp-2">{hand.hand_description}</div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`px-2 py-1 rounded text-xs font-medium ${DECISION_QUALITY_COLORS[hand.decision_quality] || DECISION_QUALITY_COLORS.pending}`}>
                                                {hand.decision_quality_display || 'Pending'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-wrap gap-1">
                                                {hand.players_involved?.slice(0, 2).map(player => (
                                                    <Link
                                                        key={player.id}
                                                        to={`/players/${player.id}`}
                                                        className="px-2 py-0.5 rounded text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800/30"
                                                    >
                                                        {player.name}
                                                    </Link>
                                                ))}
                                                {hand.players_involved?.length > 2 && (
                                                    <span className="text-xs text-gray-500">+{hand.players_involved.length - 2}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-wrap gap-1">
                                                {hand.leaks?.slice(0, 2).map(leak => (
                                                    <span key={leak.id} className="px-2 py-0.5 rounded text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300">
                                                        {leak.name}
                                                    </span>
                                                ))}
                                                {hand.leaks?.length > 2 && (
                                                    <span className="text-xs text-gray-500">+{hand.leaks.length - 2}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            {hand.amount ? (
                                                <span className={`font-medium ${hand.outcome === 'won' ? 'text-green-600' : hand.outcome === 'lost' ? 'text-red-600' : 'text-gray-600'}`}>
                                                    {hand.outcome === 'won' ? '+' : hand.outcome === 'lost' ? '-' : ''}
                                                    {formatCurrency(Math.abs(parseFloat(hand.amount)))}
                                                </span>
                                            ) : (
                                                <span className="text-gray-400 dark:text-gray-500">-</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <Link
                                                to={`/hands/${hand.id}`}
                                                className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline"
                                            >
                                                <EyeIcon className="h-4 w-4" />
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

export default HandsPage;
